// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import api from './advisorApi';
import type {
  CreatePensionWizardRequest,
  CreateProposalRequest,
  CreateQuoteBackedProposalRequest,
  PensionWizardBootstrapResult,
  Proposal,
  ProposalSection,
  ProposalStatus,
  ProposalType,
  QuoteBackedProposalBootstrapResult,
  SuitabilityStatement,
  UpdateProposalRequest,
  UpdateProposalSectionRequest,
} from '../types/proposal';
import type { PageResponse } from '../types/client';

export interface ListProposalsParams {
  page?: number;
  size?: number;
  proposalType?: ProposalType;
  status?: ProposalStatus;
  search?: string;
}

/**
 * Advisor-facing Proposals API. Mirrors the Quotes API conventions: shares the
 * axios instance from advisorApi (which sets X-Brokerage-Id), returns typed
 * payloads.
 */
export const proposalApi = {
  createProposal: async (data: CreateProposalRequest): Promise<Proposal> => {
    const response = await api.post<Proposal>('/proposals', data);
    return response.data;
  },

  listProposals: async (
    profileId: string,
    params: ListProposalsParams = {},
  ): Promise<PageResponse<Proposal>> => {
    const response = await api.get<PageResponse<Proposal>>(`/proposals/profile/${profileId}`, {
      params: {
        page: params.page ?? 0,
        size: params.size ?? 20,
        ...(params.proposalType ? { proposalType: params.proposalType } : {}),
        ...(params.status ? { status: params.status } : {}),
        ...(params.search ? { search: params.search } : {}),
      },
    });
    return response.data;
  },

  getProposal: async (id: string): Promise<Proposal> => {
    const response = await api.get<Proposal>(`/proposals/${id}`);
    return response.data;
  },

  updateProposal: async (id: string, data: UpdateProposalRequest): Promise<Proposal> => {
    const response = await api.put<Proposal>(`/proposals/${id}`, data);
    return response.data;
  },

  sendProposal: async (id: string): Promise<Proposal> => {
    const response = await api.post<Proposal>(`/proposals/${id}/send`);
    return response.data;
  },

  withdrawProposal: async (id: string, reason?: string): Promise<Proposal> => {
    const response = await api.post<Proposal>(`/proposals/${id}/withdraw`, { reason });
    return response.data;
  },

  deleteProposal: async (id: string): Promise<void> => {
    await api.delete(`/proposals/${id}`);
  },

  /**
   * Whether the pension proposal wizard is enabled for this tenant. The
   * backend 404s when the base `proposals` feature is off entirely; callers
   * should treat any rejection as "disabled" and fall back to the flat
   * pension compose modal.
   */
  pensionWizardEnabled: async (): Promise<boolean> => {
    const response = await api.get<{ enabled: boolean }>('/proposals/pension-wizard/enabled');
    return response.data.enabled;
  },

  /**
   * Step 1 submit of the pension proposal wizard: bootstraps the Quote
   * (existing quote engine) and the DRAFT proposal shell linked via
   * `source_quote_id`, in one backend transaction.
   */
  startPensionWizard: async (
    data: CreatePensionWizardRequest,
  ): Promise<PensionWizardBootstrapResult> => {
    const response = await api.post<PensionWizardBootstrapResult>('/proposals/pension-wizard', data);
    return response.data;
  },

  // -- Statement of Suitability (wizard Step 3) ---------------------------

  /**
   * Fetch (or seed on first entry) the proposal's Statement of Suitability.
   * The first call seeds all six sections server-side from the brokerage's
   * resolved templates; 422 QUOTE_OPTION_NOT_SELECTED if the wizard hasn't
   * reached this point yet.
   */
  getSuitabilityStatement: async (proposalId: string): Promise<SuitabilityStatement> => {
    const response = await api.get<SuitabilityStatement>(
      `/proposals/${proposalId}/suitability-statement`,
    );
    return response.data;
  },

  /**
   * Persist a partial set of section edits. Only the keys present in
   * `sections` are updated. Note: any edit after GENERATED reverts the
   * statement's status back to DRAFT server-side.
   */
  updateSuitabilityStatement: async (
    proposalId: string,
    sections: Record<string, string>,
  ): Promise<SuitabilityStatement> => {
    const response = await api.put<SuitabilityStatement>(
      `/proposals/${proposalId}/suitability-statement`,
      { sections },
    );
    return response.data;
  },

  /**
   * Persist an AI-drafted section along with its originating intelligence
   * session id (provenance).
   */
  applyAiSection: async (
    proposalId: string,
    sectionKey: string,
    html: string,
    sessionId: string,
  ): Promise<SuitabilityStatement> => {
    const response = await api.post<SuitabilityStatement>(
      `/proposals/${proposalId}/suitability-statement/sections/${sectionKey}/apply-ai`,
      { html, sessionId },
    );
    return response.data;
  },

  /**
   * Render + store the branded SoS PDF, stamp the advisor signature block,
   * and complete the source quote. 422 SOS_SECTION_EMPTY if any section is
   * still empty.
   */
  generateSuitabilityDocument: async (proposalId: string): Promise<SuitabilityStatement> => {
    const response = await api.post<SuitabilityStatement>(
      `/proposals/${proposalId}/suitability-statement/generate-document`,
    );
    return response.data;
  },

  /** Download the current (unsigned, or signed once available) SoS PDF. */
  getSuitabilityDocument: async (proposalId: string): Promise<Blob> => {
    const response = await api.get(`/proposals/${proposalId}/suitability-statement/document`, {
      responseType: 'blob',
    });
    return response.data;
  },

  // -- Pension Proposal Builder v2 (sectioned builder) --------------------
  // docs/specs/pension-proposal-builder-v2-prd.md §3

  /**
   * Whether the sectioned Pension Proposal Builder v2 is enabled for this
   * tenant. Mirrors {@link pensionWizardEnabled}'s any-rejection-is-disabled
   * contract; callers should fall back to the v1 wizard's Step 3 editor.
   */
  pensionBuilderEnabled: async (): Promise<boolean> => {
    const response = await api.get<{ enabled: boolean }>('/proposals/pension-builder/enabled');
    return response.data.enabled;
  },

  /**
   * Whether the BREAKTHROUGH_REPORT proposal type is enabled for this
   * tenant. Mirrors {@link pensionBuilderEnabled}'s any-rejection-is-disabled
   * contract; callers should hide the "Breakthrough Report" option in the
   * New Proposal chooser on any failure.
   */
  breakthroughReportEnabled: async (): Promise<boolean> => {
    const response = await api.get<{ enabled: boolean }>('/proposals/breakthrough-report/enabled');
    return response.data.enabled;
  },

  /**
   * Whether the Investment Proposal Builder is enabled for this tenant.
   * Mirrors {@link pensionBuilderEnabled}'s any-rejection-is-disabled
   * contract; callers should fall back to the flat FUND_INVESTMENT compose
   * modal on any failure.
   */
  investmentBuilderEnabled: async (): Promise<boolean> => {
    const response = await api.get<{ enabled: boolean }>('/proposals/investment-builder/enabled');
    return response.data.enabled;
  },

  /**
   * Whether the Protection Proposal Builder is enabled for this tenant.
   * Mirrors {@link pensionBuilderEnabled}'s any-rejection-is-disabled
   * contract; callers should fall back to the flat PROTECTION compose modal
   * on any failure.
   */
  protectionBuilderEnabled: async (): Promise<boolean> => {
    const response = await api.get<{ enabled: boolean }>('/proposals/protection-builder/enabled');
    return response.data.enabled;
  },

  /**
   * Step 1 submit of the Investment/Protection builder's Quote Request step:
   * bootstraps the Quote (existing quote engine, correct `quoteType`) and the
   * DRAFT proposal shell linked via `source_quote_id`, in one backend
   * transaction. Generalises {@link startPensionWizard} to the other two
   * quote-backed proposal types (docs/specs/investment-protection-proposals-prd.md).
   */
  startQuoteBackedProposal: async (
    data: CreateQuoteBackedProposalRequest,
  ): Promise<QuoteBackedProposalBootstrapResult> => {
    const response = await api.post<QuoteBackedProposalBootstrapResult>(
      '/proposals/quote-backed',
      data,
    );
    return response.data;
  },

  /** Fetch the proposal's sections, ordered by `sortOrder`. */
  getSections: async (proposalId: string): Promise<ProposalSection[]> => {
    const response = await api.get<ProposalSection[]>(`/proposals/${proposalId}/sections`);
    return response.data;
  },

  /**
   * Persist a section's content (and optionally its display title). DRAFT
   * proposals only — 409 if the proposal has already been sent; 422 for an
   * unknown `sectionKey`.
   */
  updateSection: async (
    proposalId: string,
    sectionKey: string,
    data: UpdateProposalSectionRequest,
  ): Promise<ProposalSection> => {
    const response = await api.put<ProposalSection>(
      `/proposals/${proposalId}/sections/${sectionKey}`,
      data,
    );
    return response.data;
  },

  /** Add an optional section (only APPENDICES for PENSION proposals). */
  addSection: async (proposalId: string, sectionKey: string): Promise<ProposalSection> => {
    const response = await api.post<ProposalSection>(
      `/proposals/${proposalId}/sections/${sectionKey}`,
    );
    return response.data;
  },

  /** Remove an optional section (only APPENDICES for PENSION proposals). */
  removeSection: async (proposalId: string, sectionKey: string): Promise<void> => {
    await api.delete(`/proposals/${proposalId}/sections/${sectionKey}`);
  },

  /**
   * Attach (or replace, while DRAFT) the cashflow scenario backing the
   * CASHFLOW_IMPACT section. Changing scenario clears the section's
   * `narrative` + `snapshot` server-side; re-posting the same scenario with a
   * new caption just updates the caption.
   */
  setCashflowScenario: async (
    proposalId: string,
    scenarioId: string,
    caption?: string,
  ): Promise<ProposalSection> => {
    const response = await api.post<ProposalSection>(
      `/proposals/${proposalId}/sections/cashflow-impact/scenario`,
      { scenarioId, ...(caption !== undefined ? { caption } : {}) },
    );
    return response.data;
  },

  /**
   * Persist an AI-drafted block of a section along with its originating
   * intelligence session id (provenance). Valid (sectionKey, block) pairs:
   * (NEEDS_OBJECTIVES, needsObjectives), (PENSION_RECOMMENDATION, rationale),
   * (CASHFLOW_IMPACT, narrative).
   */
  applyAiBlock: async (
    proposalId: string,
    sectionKey: string,
    block: string,
    payload: Record<string, unknown>,
    sessionId: string,
  ): Promise<ProposalSection> => {
    const response = await api.post<ProposalSection>(
      `/proposals/${proposalId}/sections/${sectionKey}/blocks/${block}/apply-ai`,
      { payload, sessionId },
    );
    return response.data;
  },

  /**
   * Share the builder proposal with the client: freezes the plan, generates
   * the Statement of Suitability and sends it. 422 with a message starting
   * "SECTIONS_INCOMPLETE: ..." / "RECOMMENDATION_STALE: ..." when not ready;
   * 409 when the proposal is no longer a draft.
   */
  shareProposal: async (proposalId: string): Promise<Proposal> => {
    const response = await api.post<Proposal>(`/proposals/${proposalId}/share`);
    return response.data;
  },
};
